import { useEffect, useRef } from 'react';
import type { User } from '@klareospace/pharos-lib-types';
import type { MasterContextValue } from '&/master';
import { useMasterContext } from '&/master';
import type { UseUserScopesReturn } from './use-user-scopes';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UseMasterAutoInitParams {
  scopeInfo: UseUserScopesReturn;
  user: User | null | undefined;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SESSION_STORAGE_KEY = 'pharos:master-scope';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readRawPersistedScope(): { siteId: string | null; spaceId: string | null } | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    const spaceId = parsed['spaceId'];
    const siteId = parsed['siteId'];
    const isSpaceIdValid = spaceId === null || typeof spaceId === 'string';
    const isSiteIdValid = siteId === null || typeof siteId === 'string';
    if (!isSpaceIdValid || !isSiteIdValid) return null;
    return {
      siteId: typeof siteId === 'string' ? siteId : null,
      spaceId: typeof spaceId === 'string' ? spaceId : null,
    };
  } catch {
    return null;
  }
}

/**
 * Validates a persisted scope against the user's current scopes/permissions.
 * Returns true when the persisted scope is still valid and should be kept.
 * Returns false when the scope is stale/invalid and auto-init should proceed.
 */
function isPersistedScopeValid(
  persisted: { siteId: string | null; spaceId: string | null },
  scopeInfo: UseUserScopesReturn,
): boolean {
  // If nothing is persisted (both null) → let auto-init run
  if (persisted.spaceId === null && persisted.siteId === null) return false;

  return !hasStaleScope(persisted, scopeInfo);
}

function hasStaleScope(
  persisted: { siteId: string | null; spaceId: string | null },
  { singleSiteId, singleSpaceId }: UseUserScopesReturn,
): boolean {
  if (hasStaleSiteId(persisted.siteId, singleSiteId)) return true;
  if (hasStaleSiteSpaceId({ persistedSpaceId: persisted.spaceId, singleSiteId, singleSpaceId })) return true;
  return hasStaleSpaceOnlyId({
    persistedSpaceId: persisted.spaceId,
    singleSiteId,
    singleSpaceId,
  });
}

function hasStaleSiteId(persistedSiteId: string | null, singleSiteId: string | null): boolean {
  return !!singleSiteId && !!persistedSiteId && persistedSiteId !== singleSiteId;
}

interface HasStaleSiteSpaceIdParams {
  persistedSpaceId: string | null;
  singleSiteId: string | null;
  singleSpaceId: string | null;
}

function hasStaleSiteSpaceId({ persistedSpaceId, singleSiteId, singleSpaceId }: HasStaleSiteSpaceIdParams): boolean {
  return !!singleSiteId && !!persistedSpaceId && !!singleSpaceId && persistedSpaceId !== singleSpaceId;
}

interface HasStaleSpaceOnlyIdParams {
  persistedSpaceId: string | null;
  singleSiteId: string | null;
  singleSpaceId: string | null;
}

function hasStaleSpaceOnlyId({ persistedSpaceId, singleSiteId, singleSpaceId }: HasStaleSpaceOnlyIdParams): boolean {
  return !!singleSpaceId && !singleSiteId && !!persistedSpaceId && persistedSpaceId !== singleSpaceId;
}

function runAutoInit(scopeInfo: UseUserScopesReturn, context: MasterContextValue): void {
  const { firstSpaceId, hasMultipleSites, hasMultipleSpaces, isTenantScoped, singleSiteId, singleSpaceId } = scopeInfo;
  const { selectSite, selectSpace } = context;

  // 1. Single-site user → auto-select that site
  if (singleSiteId) {
    if (!singleSpaceId) return;
    selectSite(singleSiteId, singleSpaceId);
    return;
  }

  // 2. Single-space user → auto-select the space (scope-based, not permission-based).
  // This covers both space admins AND multi-site operators who are scoped to a single
  // parent space. The space scope determines the initial context — permissions only
  // control what settings pages they can access, not the MasterContext initialization.
  if (singleSpaceId) {
    selectSpace(singleSpaceId);
    return;
  }

  // 3. Cross-space-sites user → auto-select first parent space.
  // These users have sites spanning multiple parent spaces. Defaulting to tenant
  // would require tenant settings access they don't have, so we select their
  // first available parent space.
  if (firstSpaceId && hasMultipleSites && !isTenantScoped) {
    selectSpace(firstSpaceId);
    return;
  }

  // 4. Tenant/multi-space admins → no-op (stay at tenant level)
  if (isTenantScoped || hasMultipleSpaces) return;

  // Fallback → do nothing
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Auto-initializes MasterContext on first load (when sessionStorage is empty)
 * or after a persisted scope validation failure.
 *
 * Cascade (scope-based, not permission-based):
 * 1. singleSiteId + singleSpaceId → selectSite(singleSiteId, singleSpaceId)
 * 2. singleSpaceId → selectSpace(singleSpaceId)
 * 3. firstSpaceId + hasMultipleSites + !isTenantScoped → selectSpace(firstSpaceId)
 * 4. tenant/multiSpace → no-op (stay at tenant level)
 *
 * Also validates existing persisted scope against current user scopes.
 * Invalid scopes (stale, from another user) are cleared and auto-init runs.
 */
export function useMasterAutoInit({ scopeInfo, user }: UseMasterAutoInitParams): void {
  const masterContext = useMasterContext();
  const hasRunRef = useRef(false);

  useEffect(() => {
    // Wait for scope data to be ready
    if (scopeInfo.isLoading || !user) return;

    // Only run once per mount (or until reset by the dep below)
    if (hasRunRef.current) return;

    hasRunRef.current = true;

    const persisted = readRawPersistedScope();

    // If there is a non-empty persisted scope, validate it
    const hasPersistedScope = persisted !== null && (persisted.spaceId !== null || persisted.siteId !== null);
    if (hasPersistedScope) {
      if (isPersistedScopeValid(persisted, scopeInfo)) {
        // Persisted scope is valid — keep it, do not auto-init
        return;
      }
      // Invalid scope — clear it and proceed to auto-init
      masterContext.clearScope();
    }

    runAutoInit(scopeInfo, masterContext);
  }, [scopeInfo, masterContext, user]);
}
